import { format, parseISO } from 'date-fns';
import { getExpenseCategory, getCategoryStyle, formatCurrency } from '../../utils/categories';
import { getCategoryIcon } from '../UI/Icons';

/**
 * ExpenseRow - Desktop table row for a single expense
 */
export default function ExpenseRow({
  expense,
  isEditing,
  editAmount,
  onEditAmountChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onTogglePlanned,
  onDelete,
}) {
  const category = getExpenseCategory(expense.category);
  const categoryStyle = getCategoryStyle(category.colorVar);

  return (
    <tr 
      className="transition-colors"
      style={{ borderBottom: '1px solid var(--color-border-subtle)' }}
    >
      <td className="px-4 py-3">
        <div className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
          {format(parseISO(expense.expense_date), 'dd MMM yyyy')}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
          {expense.description}
        </div>
        {expense.event_title && (
          <div className="text-xs flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
            {getCategoryIcon(expense.event_category || 'activity', { size: 12 })}
            {expense.event_title}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          <span style={categoryStyle} className="p-1 rounded">
            {getCategoryIcon(expense.category, { size: 16 })}
          </span>
          {category.label}
        </span>
      </td>
      <td className="px-4 py-3">
        <button
          onClick={() => onTogglePlanned(expense)}
          className="badge cursor-pointer transition-colors"
          style={expense.is_planned ? {
            backgroundColor: 'rgba(189, 166, 110, 0.15)',
            color: 'var(--color-category-6)',
          } : {
            backgroundColor: 'rgba(78, 143, 106, 0.15)',
            color: 'var(--color-status-success)',
          }}
        >
          {expense.is_planned ? 'Planned' : 'Actual'}
        </button>
      </td>
      <td className="px-4 py-3 text-right">
        {isEditing ? (
          <input
            type="number"
            value={editAmount}
            onChange={(e) => onEditAmountChange(e.target.value)}
            className="input w-24 text-right text-sm py-1"
            min="0"
            step="0.01"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSaveEdit(expense);
              if (e.key === 'Escape') onCancelEdit();
            }}
          />
        ) : (
          <div 
            className="text-sm font-semibold cursor-pointer"
            style={{ color: 'var(--color-text-primary)' }}
            onClick={() => onStartEdit(expense)}
            title="Click to edit"
          >
            {formatCurrency(expense.amount, expense.currency)}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          {isEditing ? (
            <>
              <button
                onClick={() => onSaveEdit(expense)}
                className="p-1.5 rounded transition-colors"
                style={{ color: 'var(--color-status-success)' }}
                title="Save"
              >
                <CheckIcon className="w-4 h-4" />
              </button>
              <button
                onClick={onCancelEdit}
                className="p-1.5 rounded transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
                title="Cancel"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => onStartEdit(expense)}
                className="p-1.5 rounded transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
                title="Edit amount"
              >
                <EditIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(expense)}
                className="p-1.5 rounded transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
                title="Delete"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function TrashIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function EditIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
